// Artikel utilities for generating outline and managing content
import linkupService from '../../services/linkupService'
import { MODEL_MODES } from '../../constants/agentConfig'

// Generate artikel outline based on query/topic using LLM
export const generateArtikelOutline = async (topic, llmService) => {
  let academicContext = ''
  let academicSources = []
  
  try {
    // Lakukan pencarian untuk mendapatkan konteks yang relevan
    console.log(`🔍 Mencari konteks untuk topik artikel: ${topic}`);
    const searchResults = await linkupService.getScientificInfo(`${topic} artikel web content structure`);
    
    if (searchResults && searchResults.sources && searchResults.sources.length > 0) {
      academicSources = searchResults.sources.slice(0, 20); // Simpan 20 sumber untuk digunakan dalam artikel
      const topSources = academicSources.slice(0, 5);
      academicContext = `\n\nKonteks Referensi:\n${topSources.map((source, index) => 
        `${index + 1}. ${source.title}\n   ${source.snippet}`
      ).join('\n')}`;
      console.log(`✅ Ditemukan ${academicSources.length} sumber untuk outline artikel`);
    }
  } catch (error) {
    console.warn('❌ Error saat mencari konteks:', error.message);
  }
  
  const prompt = `Buatkan OUTLINE (struktur poin-poin) artikel web untuk topik "${topic}" dalam format JSON dengan struktur berikut:
  {
    "sections": [
      {
        "id": "pendahuluan",
        "title": "Pendahuluan",
        "description": "Deskripsi singkat isi bagian",
        "minWords": 300,
        "isEditable": true,
        "subsections": ["Hook", "Latar Belakang", "Tujuan Artikel"]
      }
    ]
  }
  
  PENTING: Ini adalah OUTLINE (kerangka/struktur), BUKAN konten lengkap artikel!
  
  WAJIB SERTAKAN BAGIAN-BAGIAN BERIKUT (TIDAK BOLEH DIHILANGKAN):
  1. Pendahuluan
  2. Pembahasan Utama (bisa dibagi menjadi beberapa sub-bagian)
  3. Kesimpulan
  4. Referensi (id: "referensi")
  
  Pedoman:
  - Ikuti struktur artikel web yang engaging dan mudah dibaca
  - Berikan ID unik untuk setiap bagian (contoh: pendahuluan, pembahasan_1, kesimpulan, referensi)
  - Sertakan subsections (sub-bagian) untuk setiap bagian utama
  - Description berisi penjelasan singkat apa yang akan dibahas di bagian tersebut
  - TARGET TOTAL KATA: MAKSIMAL 3000 KATA (distribusi kata harus proporsional)
  - Sesuaikan dengan topik: "${topic}"
  - PASTIKAN Referensi SELALU ada di akhir outline
  - Pertimbangkan konteks yang relevan jika tersedia${academicContext}
  
  Berikan HANYA JSON tanpa penjelasan tambahan:`
  
  try {
    // Use GENIUS mode for comprehensive writing
    const response = await llmService.sendMessage(
      [{ role: 'user', content: prompt }],
      { 
        mode: MODEL_MODES.GENIUS,
        searchType: 'web',
        stream: false,
        consumeCredits: false
      }
    )
    const cleanedResponse = response.replace(/```json|```/g, '').trim()
    const outline = JSON.parse(cleanedResponse)
    
    if (outline && outline.sections && Array.isArray(outline.sections)) {
      // Pastikan setiap section memiliki ID dan struktur yang benar
      let processedSections = outline.sections.map((section, index) => ({
        id: section.id || `section_${index + 1}`,
        title: section.title,
        description: section.description || '',
        minWords: section.minWords || 400,
        isEditable: section.isEditable !== false,
        subsections: section.subsections || [],
        topic: topic
      }))
      
      // Distribusi ulang kata untuk memastikan total maksimal 3000 kata
      processedSections = redistributeWordCount(processedSections)
      
      // Validasi dan tambahkan bagian wajib jika tidak ada
      const requiredSections = [
        {
          id: 'referensi',
          title: 'Referensi',
          minWords: 0,
          description: 'Daftar sumber dan referensi yang digunakan dalam artikel',
          isEditable: false
        }
      ]
      
      // Periksa dan tambahkan bagian yang hilang
      requiredSections.forEach(requiredSection => {
        const exists = processedSections.some(section => section.id === requiredSection.id)
        if (!exists) {
          console.log(`⚠️ Menambahkan bagian yang hilang: ${requiredSection.title}`)
          processedSections.push({
            ...requiredSection,
            topic: topic
          })
        }
      })
      
      return {
        outline: processedSections,
        academicSources: academicSources
      }
    } else {
      throw new Error('Format outline tidak valid')
    }
  } catch (error) {
    console.warn('❌ Error generating outline with LLM, using fallback:', error.message);
    // Fallback ke outline default jika LLM gagal
    return {
      outline: getDefaultOutline(topic),
      academicSources: academicSources
    }
  }
}

// Fallback outline function dengan distribusi kata maksimal 3000 kata total
const getDefaultOutline = (topic) => {
  const defaultSections = [
    {
      id: 'pendahuluan',
      title: 'Pendahuluan',
      minWords: 350,
      description: 'Pembukaan artikel yang menarik perhatian pembaca',
      isEditable: true,
      subsections: ['Hook', 'Latar Belakang', 'Tujuan Artikel'],
      topic: topic
    },
    {
      id: 'pembahasan_utama',
      title: 'Pembahasan Utama',
      minWords: 1800,
      description: 'Konten utama artikel dengan pembahasan mendalam',
      isEditable: true,
      subsections: ['Konsep Dasar', 'Analisis', 'Contoh Praktis'],
      topic: topic
    },
    {
      id: 'tips_praktis',
      title: 'Tips dan Saran Praktis',
      minWords: 550,
      description: 'Panduan praktis yang dapat diterapkan pembaca',
      isEditable: true,
      subsections: ['Langkah-langkah', 'Best Practices', 'Hal yang Perlu Dihindari'],
      topic: topic
    },
    {
      id: 'kesimpulan',
      title: 'Kesimpulan',
      minWords: 300,
      description: 'Ringkasan dan penutup artikel',
      isEditable: true,
      subsections: ['Ringkasan Poin Utama', 'Call to Action'],
      topic: topic
    },
    {
      id: 'referensi',
      title: 'Referensi',
      minWords: 0,
      description: 'Daftar sumber dan referensi yang digunakan dalam artikel',
      isEditable: false,
      topic: topic
    }
  ]
  
  // Pastikan distribusi kata sesuai dengan batasan 3000 kata
  return redistributeWordCount(defaultSections)
}

// Redistribute word count to ensure maximum 3000 words total
const redistributeWordCount = (sections) => {
  const MAX_TOTAL_WORDS = 3000
  
  // Hitung total kata saat ini (kecuali referensi)
  const contentSections = sections.filter(section => section.id !== 'referensi')
  const currentTotal = contentSections.reduce((total, section) => total + (section.minWords || 0), 0)
  
  // Jika sudah sesuai atau kurang dari maksimal, kembalikan apa adanya
  if (currentTotal <= MAX_TOTAL_WORDS) {
    console.log(`✅ Total kata sudah sesuai: ${currentTotal}/${MAX_TOTAL_WORDS} kata`)
    return sections
  }
  
  // Jika melebihi, distribusi ulang secara proporsional
  const scaleFactor = MAX_TOTAL_WORDS / currentTotal
  console.log(`⚠️ Total kata melebihi batas (${currentTotal}), melakukan redistribusi dengan faktor ${scaleFactor.toFixed(2)}`)
  
  const redistributedSections = sections.map(section => {
    if (section.id === 'referensi') {
      return section // Referensi tidak dihitung dalam total kata
    }
    
    const originalWords = section.minWords || 0
    const newMinWords = Math.floor(originalWords * scaleFactor)
    const finalWords = Math.max(newMinWords, 200) // Minimal 200 kata per bagian
    
    if (originalWords !== finalWords) {
      console.log(`📝 ${section.title}: ${originalWords} → ${finalWords} kata`)
    }
    
    return {
      ...section,
      minWords: finalWords
    }
  })
  
  const newTotal = redistributedSections
    .filter(s => s.id !== 'referensi')
    .reduce((total, section) => total + (section.minWords || 0), 0)
  console.log(`✅ Redistribusi selesai: Total kata baru = ${newTotal}/${MAX_TOTAL_WORDS}`)
  
  return redistributedSections
}

// Calculate total words from outline
export const calculateTotalWords = (outline) => {
  return outline.reduce((total, section) => total + (section.minWords || 0), 0)
}

// Validate outline structure and word count
export const validateOutline = (outline) => {
  const errors = []
  
  if (!outline || outline.length === 0) {
    errors.push('Outline tidak boleh kosong')
    return { isValid: false, errors }
  }
  
  const totalWords = calculateTotalWords(outline)
  
  if (totalWords < 2000) {
    errors.push(`Total kata terlalu sedikit: ${totalWords} (minimal 2000)`)
  }
  
  if (totalWords > 3000) {
    errors.push(`Total kata terlalu banyak: ${totalWords} (maksimal 3000)`)
  }
  
  // Check for required sections
  const requiredSections = ['pendahuluan', 'pembahasan', 'kesimpulan']
  const sectionTitles = outline.map(s => s.title.toLowerCase())
  
  requiredSections.forEach(required => {
    const hasSection = sectionTitles.some(title => title.includes(required))
    if (!hasSection) {
      errors.push(`Bagian '${required}' diperlukan dalam outline`)
    }
  })
  
  return {
    isValid: errors.length === 0,
    errors,
    totalWords
  }
}

// Format progress percentage
export const formatProgress = (current, total) => {
  if (total === 0) return 0
  return Math.round((current / total) * 100)
}

// Estimate time remaining
export const estimateTimeRemaining = (current, total, startTime) => {
  if (current === 0) return 'Menghitung...'
  
  const elapsed = Date.now() - startTime
  const avgTimePerSection = elapsed / current
  const remaining = (total - current) * avgTimePerSection
  
  const minutes = Math.ceil(remaining / 60000)
  if (minutes < 1) return 'Kurang dari 1 menit'
  if (minutes === 1) return '1 menit'
  return `${minutes} menit`
}

// Parse markdown content to extract citations and clean content
export const parseMarkdownContent = (content) => {
  if (!content) return { parsedContent: '', citations: [] }
  
  // Extract citations in format [1], [2], etc.
  const citationRegex = /\[(\d+)\]/g
  const citations = []
  let match
  
  while ((match = citationRegex.exec(content)) !== null) {
    const citationNumber = parseInt(match[1])
    if (!citations.includes(citationNumber)) {
      citations.push(citationNumber)
    }
  }
  
  // For now, return content as-is (citations will be handled in display)
  return {
    parsedContent: content,
    citations: citations.sort((a, b) => a - b)
  }
}

// Format source to APA style
export const formatSourceToAPA = (source, index) => {
  if (!source) return ''
  
  const currentYear = new Date().getFullYear()
  let domain = 'Unknown'
  
  try {
    if (source.url) {
      domain = new URL(source.url).hostname.replace('www.', '')
    }
  } catch (error) {
    domain = 'Unknown'
  }
  
  // Extract author from title or use domain
  let author = domain
  if (source.title) {
    // Try to extract potential author from title (if it contains dashes or pipes)
    const titleParts = source.title.split(/[-|–—]/)
    if (titleParts.length > 1) {
      author = titleParts[titleParts.length - 1].trim()
      if (author.length < 3) author = domain
    }
  }
  
  // Capitalize first letter of author
  author = author.charAt(0).toUpperCase() + author.slice(1)
  
  // Format: Author. (Year). Title. Domain. URL
  const title = source.title || 'Untitled'
  const url = source.url || `https://${domain}`
  
  return `${author}. (${currentYear}). ${title}. <em>${domain}</em>. <a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`
}

// Count words in text
export const countWords = (text) => {
  if (!text) return 0
  return text.trim().split(/\s+/).filter(word => word.length > 0).length
}